import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Button } from '@3plearning/react-components';
import { AssignIcon } from '../../helpers/icon-dictionary';
import {
  AssignLearningJourneysWrapper,
  Header,
  Body,
  Footer,
  LearningJourneyEmptyRow
} from './styled';
import { CHECKED, DISABLED } from '../checkbox/constants';
import LearningJourneyRow from './learning-journey-row';
import {
  assignLearningGaps,
  updateLearningJourneysByIndex,
  updateLearningJourneysByState
} from '../../containers/link-upf-modal/link-upf-modal-actions';
import { showToast } from '../toast/toast-action';
import { DEFAULT_STUDENT_ID } from '../../containers/link-upf-modal/link-upf-modal-utils';

const AssignLearningJourneys = ({ i18n, data }) => {
  const {
    groupSelection,
    questions,
    showStudentLJ = false,
    studentProfileIds,
    studentId = DEFAULT_STUDENT_ID,
    studentName
  } = data;
  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const { selectedClassRoom } = useSelector(state => state.UPFAssignReducer);
  const { isLoadingLJ } = useSelector(state => state.linkUpfModalReducer);
  const allLearningJourneys = useSelector(state => state.linkUpfModalReducer.learningJourneys);
  const learningJourneys = allLearningJourneys[studentId] || [];
  const dispatch = useDispatch();

  const handleCheckbox = (state, index) => {
    dispatch(updateLearningJourneysByIndex(studentId, index, state));
  };

  const handleAssign = async () => {
    const learningJourneysToAssign = learningJourneys.filter(lj => lj.checkboxState === CHECKED);

    const hasAssigned = await dispatch(assignLearningGaps({
      productId: selectedProduct && selectedProduct.id,
      classroomId: selectedClassRoom.value,
      studentProfileIds: studentProfileIds || [studentId],
      learningJourneys: learningJourneysToAssign.map(lj => ({
        id: lj.id,
        courseId: lj.courseId,
        courseVersion: lj.courseVersion
      }))
    }));
    if (hasAssigned) {
      let message;
      if (showStudentLJ) {
        const assignedTotal = learningJourneysToAssign.length;
        message = assignedTotal + i18n.t` Understanding, Practice and Fluency activities assigned to ` + studentName;
      } else {
        message = groupSelection.value === '-1' ?
          i18n.t`UPF bundle assigned to class` :
          i18n.t`UPF bundle assigned to group`;
      }
      dispatch(
        showToast(
          message,
          'success'
        )
      );
      dispatch(updateLearningJourneysByState(CHECKED, DISABLED, studentId));
    }
  };

  let header;
  let buttonText = i18n.t`Assign to student`;
  if (!showStudentLJ) {
    header = <Header>{i18n.t`Address learning gaps with these activities`}</Header>;
    buttonText = i18n.t`Assign to group`;
  }

  let learningJourneysBody;
  if (learningJourneys && learningJourneys.length) {
    learningJourneysBody = (
      <Body>
        {learningJourneys.map(lj => (
          <LearningJourneyRow
            key={lj.index}
            i18n={i18n}
            onCheckboxChange={handleCheckbox}
            learningJourney={lj}
            questions={questions}
            showStudentLJ={showStudentLJ}
          />
        ))}
      </Body>);
  } else {
    const rows = [];
    for (let index = 0; index < 5; index += 1) {
      rows.push(
        <LearningJourneyEmptyRow key={index} >
          {index === 2 ? i18n.t`No current recommendations` : ''}
        </LearningJourneyEmptyRow>
      );
    }

    learningJourneysBody = (
      <Body>
        {rows}
      </Body>
    );
  }

  return (
    <AssignLearningJourneysWrapper showStudentLJ={showStudentLJ}>
      {header}
      {learningJourneysBody}
      <Footer>
        <Button
          className="button"
          primary
          text={buttonText}
          type={Button.LOZENGE}
          onClick={handleAssign}
          alignIcon="LEFT"
          icon={AssignIcon}
          disabled={!learningJourneys.some(lj => lj.checkboxState === CHECKED) || isLoadingLJ}
        />
      </Footer>
    </AssignLearningJourneysWrapper>
  );
};

AssignLearningJourneys.propTypes = {
  i18n: PropTypes.object,
  data: PropTypes.shape({
    groupSelection: PropTypes.object,
    questions: PropTypes.array,
    showStudentLJ: PropTypes.bool,
    studentProfileIds: PropTypes.array,
    studentId: PropTypes.number,
    studentName: PropTypes.string
  })
};

export default AssignLearningJourneys;
